import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { SignInButton } from '@innovamat/ga-features';
import { Button } from '@innovamat/glimmer-components';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  overflow: auto;
  position: relative;
  color: ${({ theme }) => theme.colors.brown.darker};
  @media (max-width: 568px) {
    display: block;
  }
`;

const FirstGradient = styled.div`
  background: radial-gradient(
    50% 50% at 50% 50%,
    #2392ab 0%,
    #81d2e4 41.5%,
    rgba(254, 250, 238, 0) 100%
  );
  background-repeat: no-repeat;
  position: fixed;
  top: -55%;
  left: -60%;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const SecondGradient = styled.div`
  background: radial-gradient(
    50% 50% at 50% 50%,
    #f93 0%,
    #ffc033 41.5%,
    rgba(254, 250, 238, 0) 100%
  );
  background-repeat: no-repeat;
  position: fixed;
  bottom: -65%;
  right: -65%;
  width: 100%;
  height: 100%;
  z-index: -1;
  @media (max-width: 568px) {
    bottom: -31%;
    height: 50%;
  }
`;

const LoginCard = styled.section`
  padding: 160px 32px 48px 32px;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  align-items: center;

  @supports (padding: env(safe-area-inset-left)) {
    padding-left: calc(32px + env(safe-area-inset-left));
    padding-right: calc(32px + env(safe-area-inset-right));
  }
`;

const LoginTitle = styled.h1`
  color: ${({ theme }) => theme.colors.brown.darker};
  text-align: center;
  font-family: Bariol;
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
`;

const RegionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const DropdownContentStyled = styled.div`
  div[role='listbox'] {
    margin-top: 8px;
    margin-bottom: 8px;
    display: flex;
    padding: 12px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 8px;
    border-radius: 4px;
    border: 2px solid ${({ theme }) => theme.colors.brown.darker};
    background: #fefaee;
    min-width: 190px;
    ::before {
      content: none;
    }
    .dropdownItem {
      padding: 0;
      background-color: transparent;
      p {
        color: ${({ theme }) => theme.colors.brown.darker} !important;
        font-family: Bariol;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 21px;
        letter-spacing: 0.15px;
      }
    }
  }
`;

const SSOLoginContainer = styled.div`
  @media (max-width: 568px) {
    min-width: 280px;
    width: 100%;
  }
`;

const PasswordLoginContainer = styled.div`
  width: 100%;
  @media (max-width: 568px) {
    min-width: 280px;
    width: 100%;
  }
`;

const LoginSection = styled.div``;

const Label = styled.label`
  font-family: Bariol;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
  height: 100%;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ theme }) => theme.colors.brown.light};
  border: none;

  &:first-child {
    margin-right: 0;
  }

  &:last-child {
    margin-left: 0;
  }
`;

const DividerText = styled.span`
  color: ${({ theme }) => theme.colors.brown.light};
  text-align: center;
  line-height: normal;
  text-transform: lowercase;
  white-space: nowrap;
  padding: 16px 0;
  font-weight: 700;
`;

const SocialSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ErrorMessageContainer = styled.div`
  display: flex;
  width: 480px;
  max-width: 740px;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 2px solid var(--red-red_darker, #d6452b);
  background: var(--red-red, #ff7458);
`;

const ErrorMessage = styled.span`
  color: #fefaee;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Bariol;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.15px;
`;

const Note = styled.p`
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
`;

const AnchorStyled = styled.a`
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: from-font;
  text-underline-offset: 0.1em;
  color: ${({ theme }) => theme.colors.brown.brown};
`;

const DemoInformation = styled.div`
  color: #9d8c81;
  text-align: center;
  font-family: Bariol;
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  @media (max-width: 568px) {
    display: inline-block;
  }
`;
const DemoTextWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 840px;
  flex-direction: column;
  align-items: center;
  gap: 48px;
`;

const KnowMore = styled.div`
  color: ${({ theme }) => theme.colors.brown.brown};
  text-align: center;
  font-family: Bariol;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 0.15px;

  @media (max-width: 568px) {
    display: inline-block;
  }
`;

const Form = styled.form`
  display: flex;
  justify-content: center;
  align-items: self-end;
  flex-direction: row;
  gap: 24px;
  @media (max-width: 568px) {
    flex-direction: column;
  }
`;

const SignInWrapper = styled.div`
  gap: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  justify-items: stretch;
  @media (max-width: 568px) {
    display: flex;
    flex-direction: column;
  }
`;

const SignInButtonAtlas = styled(SignInButton)`
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.brown.darker};
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.15px;
  gap: 8px;
  padding: 8px 12px;
  justify-content: flex-start;
  svg {
    position: unset;
  }
`;

const InputFieldAtlas = styled.div<{ isShowingPassword?: boolean }>`
  width: 100%;
  flex: 1;
  position: relative;
  div:first-child {
    position: relative;
  }
  p:first-child {
    font-family: Bariol !important;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
    letter-spacing: 0.15px;
  }
  .inputContainer {
    font-family: Bariol !important;
    border-radius: 8px;
    border: 2px solid ${({ theme }) => theme.colors.brown.darker};
    outline: none;
    color: ${({ theme }) => theme.colors.brown.brown} !important;
    background-color: ${({ theme }) => theme.colors.white} !important;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.15px;
    outline-offset: unset;
    &:hover {
      border: 2px solid ${({ theme }) => theme.colors.brown.darker} !important;
      outline: none;
    }
  }
  p:last-child {
    position: absolute;
    bottom: -30px;
    color: ${({ theme }) => theme.colors.brown.brown};
    font-family: Bariol;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
    letter-spacing: 0.15px;
  }
  button {
    box-shadow: none !important;
  }
  svg {
    path {
      stroke: ${({ theme }) => theme.colors.brown.darker};
      fill: ${({ theme }) => theme.colors.brown.darker};
    }
    ${({ isShowingPassword, theme }) =>
      !isShowingPassword &&
      css`
        path:last-of-type {
          fill: ${theme.colors.white};
          stroke: ${theme.colors.white};
        }
      `}
  }
`;

const ButtonAtlas = styled(Button)`
  display: flex;
  flex-direction: row;
  max-height: 60px !important;
  height: 70px !important;
  width: 229px;
  padding: 25px 23px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border: 4px solid ${({ theme }) => theme.colors.brown.darker};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.brown.brown};
  box-shadow: 0 4px 0 0 ${({ theme }) => theme.colors.brown.darker};

  p {
    color: #fefaee;
    font-family: Bariol;
    font-size: 30px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
  }
  &:hover:enabled {
    background: #40231c;
    outline: none;
  }
  &:active:enabled {
    background: ${({ theme }) => theme.colors.brown.darker};
    outline: none;
  }
  &:disabled {
    border-color: ${({ theme }) => theme.colors.brown.light};
    box-shadow: none;
    background-color: ${({ theme }) => theme.colors.brown.lighter};

    p {
      color: ${({ theme }) => theme.colors.brown.light};
    }

    svg {
      path {
        stroke: ${({ theme }) => theme.colors.brown.light};
      }
    }
    svg {
      path {
        fill: ${({ theme }) => theme.colors.brown.light};
      }
    }
  }
`;

const ButtonAtlasRegions = styled(ButtonAtlas)`
  box-shadow: none !important;
  color: ${({ theme }) => theme.colors.brown.darker} !important;
  background-color: transparent !important;
  padding: 12px 16px;
  display: flex;
  height: 40px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  @media (max-width: 568px) {
    margin-top: 0;
  }
`;

const ArrowIconWrapper = styled.span`
  margin-left: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  svg {
    width: 100%;
    height: 100%;
    path {
      fill: #fefaee;
    }
  }
`;

const ArrowIconWrapperLeft = styled(ArrowIconWrapper)`
  margin: 0 8px 3px 0;
  div {
    width: inherit;
    height: inherit;
    min-width: inherit;
    min-height: inherit;
  }
`;

const ArrowIcon = styled.img`
  display: inline;
  width: 16px;
  height: 16px;
`;

const DropdownSelection = styled.div<{ isPressed: boolean }>`
  display: flex;
  align-items: center;
`;

const ForgotPasswordWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  bottom: -30px;
  right: 0;
  font-weight: 700;
  font-family: Bariol;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 0.15px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: from-font;
  text-underline-offset: 0.1em;
`;

const LanguageContainer = styled.div`
  align-self: flex-end;
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  justify-content: center;

  @media (max-width: 568px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  @media (max-width: 568px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: auto;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  font-family: Bariol;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 0.15px;
  a {
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-thickness: from-font;
    text-underline-offset: 0.1em;
  }
  @media (max-width: 568px) {
    display: inline-block;
  }
`;

export {
  AnchorStyled,
  ArrowIcon,
  ArrowIconWrapper,
  ArrowIconWrapperLeft,
  BottomContainer,
  ButtonAtlas,
  ButtonAtlasRegions,
  DemoInformation,
  DemoTextWrapper,
  Divider,
  DividerLine,
  DividerText,
  DropdownContentStyled,
  DropdownSelection,
  ErrorMessage,
  ErrorMessageContainer,
  FirstGradient,
  FooterContainer,
  FooterLinks,
  ForgotPasswordWrapper,
  Form,
  InputFieldAtlas,
  KnowMore,
  Label,
  LanguageContainer,
  LoginCard,
  LoginContainer,
  LoginSection,
  LoginTitle,
  Note,
  PasswordLoginContainer,
  RegionContainer,
  SecondGradient,
  SignInButtonAtlas,
  SignInWrapper,
  SocialSection,
  SSOLoginContainer,
  TopContainer,
};
